import { OfficeReviewAdapters } from '../adapters';
import { OfficeReviewsApi } from '../clients';

/**
 * Pulls the one row out of whatever envelope it arrives in.
 *
 * GOTU-7206 specifies "the same response contract as O3", and O3's contract is
 * the paged `{ reviews, total, limit, page }` envelope — but this endpoint
 * answers for a single shift and 404s when there is nothing, which reads much
 * more like a bare row. Rather than guess between them, both are accepted.
 *
 * The last branch is the one that earns its keep: an unrecognised payload
 * becomes "no review" instead of a row of undefineds. That is not hypothetical
 * — the msw catch-all in `src/test-utils/server.js` answers every undeclared
 * GET with `[]` and a 200, and a `rating: undefined` reaching the card would
 * throw inside `RatingValue`, which calls `toFixed` on a required number.
 *
 * Narrow this to whichever shape is real once it is confirmed against a
 * deployed instance; a tolerant parser is a placeholder, not a design.
 */
const pickReviewRow = (payload) => {
  if (Array.isArray(payload)) return payload[0] ?? null;
  if (Array.isArray(payload?.reviews)) return payload.reviews[0] ?? null;

  return payload?.reviewId ? payload : null;
};

/**
 * The office-side review for one shift, or null when there isn't one.
 *
 * **A 404 is an answer, not a failure.** The endpoint returns one for a shift
 * nobody reviewed, for a malformed job id, and for a soft-deleted review — all
 * of which mean "nothing to show". Every other status propagates, so the card
 * can offer a retry instead of claiming the shift was never reviewed. Getting
 * this backwards would make an organization-service outage look like an empty
 * dataset, which is the kind of wrong an admin acts on.
 *
 * Same shape as `Awards/services/GetCycleById.js`, for the same reason.
 *
 * @param {Object} params
 * @param {string} params.jobId - The job/shift ObjectID
 * @returns {Promise<import('../types/OfficeReview').IOfficeReviewViewEntity | null>}
 */
export const getOfficeReviewByShift = async ({ jobId }) => {
  try {
    const row = pickReviewRow(await OfficeReviewsApi.getOfficeReviewByShift({ jobId }));

    return row ? OfficeReviewAdapters.toOfficeReviewViewEntity(row) : null;
  } catch (error) {
    if (error?.response?.status === 404) return null;

    throw error;
  }
};
